import { useQueryClient } from '@tanstack/react-query'
import { apiPost } from '@/api/axios-client'
import { qk } from '@/api/query-keys'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ProfilePhotoField, type PhotoValue } from '@/components/composites/profile-photo-field'
import { useIdentity } from '@/features/permissions/AbilityProvider'

/**
 * Your own photo, and only your own.
 *
 * There is no member id anywhere in this dialog, because the endpoint behind it takes none:
 * "whose photo" is answered by the token. An admin who can change a colleague's ROLE cannot
 * change their face, and that is deliberate — they are different kinds of thing.
 *
 * CONTROLLED, and rendered outside the account menu that opens it. As a child of the
 * dropdown its whole subtree unmounted when the menu closed — taking the upload in flight
 * and any message it had produced with it. Somebody who closed the menu to go and look at
 * the top bar destroyed the only thing that could have told them what happened.
 *
 * Opened from the account menu rather than sat on a settings page. It is the one piece of
 * self-service in an admin area that is otherwise about other people, and the avatar in the
 * top bar is where somebody looks when they want to change it.
 */
export function ProfilePhotoDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { displayName, photoUrl } = useIdentity()
  const qc = useQueryClient()

  /**
   * Returns what the API says the photo is NOW — a signed, loadable URL, or null.
   *
   * The response used to be discarded in favour of refetching, which meant the one authority
   * on whether the write took effect was thrown away. If anything downstream then reported
   * "no photo", the dialog had no way to tell that apart from never having been asked to set
   * one, and showed the default avatar in silence.
   */
  async function save(key: PhotoValue): Promise<string | null> {
    const saved = await apiPost<{ photoUrl: string | null }>('/members/me/photo', { key })

    /*
     * Both caches, because the same face is served from two places.
     *
     * `abilities` is where the top bar reads it — the row the person is looking at while they
     * do this. `members` is every list that draws them, and leaving it stale means a photo
     * that changed in the corner of the screen but not in Settings two clicks away.
     */
    await Promise.all([
      qc.invalidateQueries({ queryKey: qk.abilitiesPrefix() }),
      qc.invalidateQueries({ queryKey: qk.members() }),
    ])
    return saved?.photoUrl ?? null
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Profile photo</DialogTitle>
          <DialogDescription>
            This is how you appear on work items, approvals and every list of people.
          </DialogDescription>
        </DialogHeader>

        <ProfilePhotoField
          currentUrl={photoUrl}
          displayName={displayName || 'You'}
          onSaved={save}
        />
      </DialogContent>
    </Dialog>
  )
}
